import React, {useMemo} from 'react';
import {Redirect, Route, Switch} from 'react-router';

import {IFrameAccessDenied} from '../components/AccessDenied';
import NoMatch from '../components/NoMatch';
import {auth} from '../setup';
import {useStatsigGateWorkspaceConsolidation} from '../util/rampFeatureFlags';
import {loginPage} from '../util/urls';
import {isInIframe} from '../util/window';
import {makeRoutes} from './makeRoutes';
import {getRouteTab, TrackRouteComponent} from './TrackRouteComponent';

// When making changes to this file, please make sure to test routes and ensure performance does not regress.
// Changes to this file have a wide impact on the app and across multiple teams.
export const AppRoutes = React.memo(() => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const filteredRoutes = useMemo(
    () =>
      makeRoutes(isWorkspaceConsolidationEnabled).filter(route =>
        'condition' in route ? route.condition !== false : true
      ),
    [isWorkspaceConsolidationEnabled]
  );

  return (
    <Switch>
      {filteredRoutes.map(route => {
        // Do not use the index as the key because this is NOT a static list of routes.
        // These are filtered down based on `condition`
        const key = Array.isArray(route.path)
          ? route.path.join('|')
          : route.path;

        if ('redirect' in route) {
          return <Redirect key={key} from={route.path} to={route.redirect} />;
        }

        const {RouteComponent} = route;

        return (
          <Route
            key={key}
            exact={route.exact}
            path={route.path}
            render={routeRenderProps => {
              // If we do not want to track this route, just render component
              if (route.skipTracking) {
                return <RouteComponent />;
              }

              const shouldBlockIframe =
                isInIframe() && !route.additionalProps?.allowIframes;

              const tab = getRouteTab(
                routeRenderProps.match,
                route.additionalProps
              );

              // Type guard to check if the route is private
              if ('isPrivateRoute' in route && route.isPrivateRoute === true) {
                if (!auth.loggedIn()) {
                  return (
                    <Redirect
                      to={{
                        pathname: loginPage(),
                        state: {from: routeRenderProps.location},
                      }}
                    />
                  );
                }

                return (
                  <TrackRouteComponent
                    routeName={route.additionalProps?.routeName}
                    tabParam={tab}>
                    {shouldBlockIframe ? (
                      <IFrameAccessDenied />
                    ) : (
                      <RouteComponent
                        additionalProps={route.additionalProps}
                        allowAnonymous={false}
                        allowIframes={route.additionalProps?.allowIframes}
                        match={routeRenderProps.match}
                        routeName={route.additionalProps?.routeName}
                      />
                    )}
                  </TrackRouteComponent>
                );
              }

              // Type guard to check if the route is public
              if ('isPrivateRoute' in route && route.isPrivateRoute === false) {
                return (
                  <TrackRouteComponent
                    routeName={route.additionalProps?.routeName}
                    tabParam={tab}>
                    {shouldBlockIframe ? (
                      <IFrameAccessDenied />
                    ) : (
                      <RouteComponent
                        tab={route.additionalProps?.tab}
                        allowAnonymous={true}
                        allowIframes={route.additionalProps?.allowIframes}
                        match={routeRenderProps.match}
                        routeName={route.additionalProps?.routeName}
                      />
                    )}
                  </TrackRouteComponent>
                );
              }

              // This should never happen. If it does, it's a bug.
              console.error('Invalid route configuration:', route);
              return null;
            }}
          />
        );
      })}
      {/* Catch-all route: Rendered when no other routes match (404 page) */}
      <TrackRouteComponent routeName="NoMatch">
        <NoMatch />
      </TrackRouteComponent>
    </Switch>
  );
});
